import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useLocalStorage } from 'react-use';
import { getThemeOptions } from './dispatch-theme/DispatchThemeOptions';

/**
 * localStorage key that persists the user's theme choice per browser.
 *
 * Versioned (`-v2`) to make AC4 ("a browser that never stored a choice renders
 * light even when the OS is dark") robust by construction. The pre-versioning
 * key {@link LEGACY_THEME_MODE_STORAGE_KEY} was also written by the now-removed
 * `useThemeToggle` hook, which seeded it from `prefers-color-scheme`. In
 * practice that seed never persisted — react-use only writes a *truthy* initial
 * value and MUI's `useMediaQuery` returns `false` on the first React-17 render,
 * so the OS-dark value never reached the write, and the old toggle that could
 * have written it was never mounted. Versioning regardless means any legacy
 * value (however it got there) is ignored and cannot resurface as a choice the
 * user never made.
 */
const THEME_MODE_STORAGE_KEY = 'dispatch-theme-is-darkmode-v2';

/** Pre-versioning key, retired on mount so no orphaned value lingers. */
const LEGACY_THEME_MODE_STORAGE_KEY = 'dispatch-theme-is-darkmode';

/**
 * Value exposed to consumers of the single theme-mode owner.
 */
type ThemeModeContextValue = {
  /** `true` when dark mode is active. */
  isDark: boolean;
  /** Flips light↔dark and persists the choice. */
  toggle: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
  undefined
);

/**
 * The SINGLE owner of the app's theme mode.
 *
 * Holds exactly one `useLocalStorage` instance (react-use), builds the MUI
 * theme from `getThemeOptions(mode)`, renders the MUI `ThemeProvider`, and
 * exposes `{ isDark, toggle }` via {@link useThemeMode}. Both the MUI theme and
 * every toggle read this one provider so a flip propagates in-tab without a
 * reload.
 *
 * Default is light. The mode is NOT seeded from `prefers-color-scheme`; a user
 * who never chose sees light even when the OS is dark (AC4). The storage key is
 * versioned so a value left by the pre-versioning key can never be misread as a
 * choice — see {@link THEME_MODE_STORAGE_KEY}. If storage is blocked or the
 * stored value is unreadable, react-use falls back to the initial value
 * (`false` → light) without throwing.
 *
 * @param props.children - the app subtree that consumes the theme
 */
export function ThemeModeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [storedIsDark, setStoredIsDark] = useLocalStorage<boolean>(
    THEME_MODE_STORAGE_KEY,
    false
  );

  // Retire the pre-versioning key once per mount so no orphaned value lingers.
  // Guarded because storage access can throw (private mode / locked-down
  // browser) — there is simply nothing to clean up in that case.
  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_THEME_MODE_STORAGE_KEY);
    } catch {
      // storage unavailable; no legacy value to remove
    }
  }, []);

  const isDark = storedIsDark ?? false;

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      isDark,
      toggle: () => setStoredIsDark(!isDark),
    }),
    [isDark, setStoredIsDark]
  );

  const theme = useMemo(
    () => createTheme(getThemeOptions(isDark ? 'dark' : 'light')),
    [isDark]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

/**
 * Reads the single theme-mode owner. Must be called inside a
 * {@link ThemeModeProvider}.
 *
 * @returns `{ isDark, toggle }`
 * @throws if called outside a {@link ThemeModeProvider}
 */
export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}
